import FundOpportunityCardData from '../../models/FundOpportunityCardData';
import { FootNoteProps } from '../../models/TopLevelTemplateComponentProps';
import sanitizeHTML from '../../services/sanitizeHTML';
import {
  PropertyCardContentManagement,
  PropertyCardCustom,
  PropertyCardProps,
  PropertyCardWithData,
} from './propertyCardTypes';

export function asContentManagement(props: PropertyCardProps): PropertyCardContentManagement {
  return props as PropertyCardContentManagement;
}

export function asDisplayProps(
  props: PropertyCardProps,
): PropertyCardCustom & PropertyCardWithData {
  return props as PropertyCardCustom & PropertyCardWithData;
}

export function deriveImageUrl(props: PropertyCardProps): string | null {
  if ('cardData' in props && props.cardData) {
    return props.cardData.image ?? '/assets/coming_soon.jpg';
  }
  if ('imageUrl' in props) {
    return props.imageUrl ?? '/assets/coming_soon.jpg';
  }
  return '/assets/coming_soon.jpg';
}

export function getDescriptionFromProps(props: PropertyCardProps): string {
  if ('cardData' in props && props.cardData) {
    return props.cardData.description || '';
  }
  if ('description' in props) {
    return props.description || '';
  }
  return '';
}

export function extractYouTubeEmbedUrl(url: string): string {
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (match && match[1]) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }
  return url;
}

const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com']);
const VIMEO_PAGE_PATH = /(?:^|\/)(\d+)(?:\/([0-9a-f]{6,}))?\/?$/i;

/**
 * Converts a vimeo.com page link (including channel/group paths and unlisted
 * `/{id}/{hash}` links) to its player.vimeo.com embed URL, carrying the
 * unlisted-video hash across as `?h=`. Anything else is returned unchanged.
 */
export function extractVimeoEmbedUrl(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  if (!VIMEO_HOSTS.has(parsed.hostname.toLowerCase())) return url;
  const match = parsed.pathname.match(VIMEO_PAGE_PATH);
  if (!match) return url;
  const hash = match[2] ?? parsed.searchParams.get('h');
  return `https://player.vimeo.com/video/${match[1]}${hash ? `?h=${hash}` : ''}`;
}

export function extractExternalVideoEmbedUrl(url: string): string {
  const youtube = extractYouTubeEmbedUrl(url);
  return youtube !== url ? youtube : extractVimeoEmbedUrl(url);
}

function hasContentBefore(node: Node): boolean {
  for (let sibling = node.previousSibling; sibling; sibling = sibling.previousSibling) {
    if (sibling.nodeType !== Node.TEXT_NODE || sibling.textContent?.trim()) return true;
  }
  return false;
}

export function parseHtmlDescription(html: string): string {
  if (!html) return '';

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  if (tempDiv.innerText.trim() === '') {
    return '';
  }

  const pElements = tempDiv.querySelectorAll('p');
  pElements.forEach((pElement) => {
    // Break only between paragraphs that share a parent: a <p> opening a list item (as the rich
    // text editor saves bullets) would otherwise render as an empty bullet with its text below.
    if (hasContentBefore(pElement)) {
      const br = document.createElement('br');
      const br2 = document.createElement('br');
      pElement.parentNode?.insertBefore(br, pElement);
      pElement.parentNode?.insertBefore(br2, pElement);
    }

    while (pElement.firstChild) {
      pElement.parentNode?.insertBefore(pElement.firstChild, pElement);
    }

    pElement.parentNode?.removeChild(pElement);
  });

  return tempDiv.innerHTML;
}

export function processWithFootnotes(
  content: string | undefined,
  footnotes: FootNoteProps[] | undefined,
): string | undefined {
  if (content && footnotes) {
    return sanitizeHTML(content, { footnotes });
  }
  return content;
}

export function buildFinalLink(
  linkable: boolean | undefined,
  linkTo: string | undefined,
  cardData: FundOpportunityCardData | undefined,
  routePrefix: string | undefined,
): string | null {
  if (!linkable) {
    return null;
  }

  if (cardData) {
    const id = cardData.fundOpportunity.id;

    if (linkTo === 'update') {
      if (routePrefix) {
        let portfolioPrefix = routePrefix.replace(/\/$/, '');
        if (portfolioPrefix.includes('/opportunities')) {
          portfolioPrefix = portfolioPrefix.replace('/opportunities', '/portfolio');
        }
        if (portfolioPrefix.includes('/portfolio')) {
          return `${portfolioPrefix}/opportunities/${id}`;
        }
        return `${portfolioPrefix}/portfolio/opportunities/${id}`;
      }
      return `/portfolio/opportunities/${id}`;
    }

    const base = routePrefix || '/opportunities';
    const cleanBase = base.replace(/\/$/, '');
    return [cleanBase, id].join('/');
  }

  if (linkTo) {
    return `/${linkTo}`;
  }

  return null;
}

export function buildCardContentHtmlForMeasurement(options: {
  processedTitle: string | undefined;
  processedDescription: string | undefined;
  derivedImage: string;
  isVideo: boolean;
  isExpanded: boolean;
  imageNaturalHeight: number | null;
  imageNaturalWidth: number | null;
}): string {
  const {
    processedTitle,
    processedDescription,
    derivedImage,
    isVideo,
    isExpanded,
    imageNaturalHeight,
  } = options;

  let html = '<div style="border: 1px solid #E0E0E0; border-radius: 1.25rem; padding: 1rem;">';
  html += `<div style="font-family: Inter, sans-serif; font-size: 1rem; font-weight: 600; margin-bottom: 1rem;">${processedTitle || ''}</div>`;

  if (derivedImage && !isVideo) {
    if (isExpanded && imageNaturalHeight) {
      const expandedHeight = Math.min(imageNaturalHeight, 400);
      html += `<div style="width: 100%; height: ${expandedHeight}px; background: #f0f0f0; margin-bottom: 1rem;"></div>`;
    } else {
      html +=
        '<div style="width: 100%; height: 200px; background: #f0f0f0; margin-bottom: 1rem;"></div>';
    }
  }

  if (processedDescription) {
    const parsedDescription = parseHtmlDescription(processedDescription);
    html += `<div style="font-family: Inter, sans-serif; font-size: 0.875rem; line-height: 1.5; color: #4F4F4F;">${parsedDescription}</div>`;
  }

  html += '</div>';
  return html;
}
